import React, { useState, useEffect, useCallback, useMemo } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import ConfirmModal from "./ConfirmModal";
import CompanyDetailsTab from "./CompanyDetailsTab";
import CompanyLogoEditor from "./MyCompanyLogo";
import PlayerProfileModal from "./PlayerProfilePanel";
import CompanyIssueTab from "./CompanyIssueTab";
import FoundCompanyTab from "./FoundCompanyTab";
import CompanyDividendsTab from "./CompanyDividendsTab";
import { useNotifications } from "../hooks/notificationsContext";

export default function MyCompany() {
  const [windowState, setWindowState] = useState("Roster");
  const [userId, setUserId] = useState(null);
  const [userCEO, setUserCEO] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rosterLoaded, setRosterLoaded] = useState(false);
  const [currentUserDisplayName, setCurrentUserDisplayName] = useState("");

  const [companyDoc, setCompanyDoc] = useState(null);
  const [, setCompanyLoading] = useState(true);

  // Modal & Target States
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResignConfirm, setShowResignConfirm] = useState(false);
  const [showSuccessorConfirm, setShowSuccessorConfirm] = useState(false);
  
  const [employeeToDismiss, setEmployeeToDismiss] = useState(null);
  const [successorTarget, setSuccessorTarget] = useState(""); 
  const [playerSelected, setPlayerSelected] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { addNotification } = useNotifications();

const [globalTaxRate, setGlobalTaxRate] = useState(0);

// Fetch Global Tax Rate from Firestore
useEffect(() => {
  async function fetchGlobalVars() {
    try {
      const globalDocRef = doc(db, "Global_Vars", "Main");
      const globalDocSnap = await getDoc(globalDocRef);
      if (globalDocSnap.exists()) {
        // Store as a decimal (e.g., 10 becomes 0.10)
        setGlobalTaxRate(globalDocSnap.data().globalBaseTaxRate / 100);
      }
    } catch (err) { 
      console.error("Failed to fetch tax rate:", err); 
    }
  }
  fetchGlobalVars();
}, []);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      setUserId(user ? user.uid : null);
    });
    return () => unsub();
  }, []);

  const fetchUserAndCompany = useCallback(async () => {
    if (!userId) return;
    setCompanyLoading(true);
    try {
      const userDocSnap = await getDoc(doc(db, "Users", userId));
      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        setUserCEO(data.userCEO === true);
        setCurrentUserDisplayName(data.userDisplayName || "");

        if (data.userCompanyTicker && data.userCompanyTicker !== "NONE") {
          const compDocSnap = await getDoc(doc(db, "Companies", data.userCompanyTicker));
          if (compDocSnap.exists()) {
            setCompanyDoc({ id: compDocSnap.id, ...compDocSnap.data() });
          } else {
            setCompanyDoc(null);
          }
        } else {
          setCompanyDoc(null);
        }
      }
    } catch (err) {
      console.error("Error fetching company data:", err);
    } finally {
      setCompanyLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserAndCompany();
  }, [userId, fetchUserAndCompany]);

  const fetchRoster = useCallback(async () => {
    if (!userId || !companyDoc) return;
    setLoading(true);
    try {
      const token = await auth.currentUser.getIdToken(true);
      const res = await fetch("https://getcompanyroster-5dhfwyjvzq-uc.a.run.app", {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const rosterData = await res.json();
      setEmployees(rosterData || []);
      setRosterLoaded(true);
    } catch (err) {
      console.error("Error fetching company roster:", err);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [userId, companyDoc]);

  useEffect(() => {
    if (windowState === "Roster" && !rosterLoaded && companyDoc) {
      fetchRoster();
    }
  }, [windowState, rosterLoaded, companyDoc, fetchRoster]);

const { relinquishFee } = useMemo(() => {
  const price = Number(companyDoc?.companyStockPrice) || 0;
  const volume = Number(companyDoc?.companyStockVolume) || 0;
  const taxDecimal = Number(globalTaxRate) || 0; 

  const mCap = price * volume;

  // Backend: (Tax/100) * MarketCap * 0.65 rounded to 2 decimals
  const fee = Math.round(taxDecimal * mCap * 0.65 * 100) / 100;
  
  return { relinquishFee: fee };
}, [companyDoc, globalTaxRate]);

  // --- API CALL: RESIGN OR TRANSFER ---
  const executeResignOrTransfer = async (targetName = "") => {
    setIsProcessing(true);
    try {
      const token = await auth.currentUser.getIdToken(true);
      const res = await fetch("https://companyresign-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: targetName }), 
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Operation failed");

      addNotification(data.message || "Protocol executed successfully.");
      window.location.reload(); 
    } catch (err) {
      addNotification(`Error: ${err.message}`);
    } finally {
      setIsProcessing(false);
      setShowResignConfirm(false);
      setShowSuccessorConfirm(false);
    }
  };

  const dismissEmployee = async () => {
    if (!userCEO || !employeeToDismiss) return;
    setIsProcessing(true);
    try {
      const token = await auth.currentUser.getIdToken(true);
      const res = await fetch("https://dismissemployee-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: employeeToDismiss }),
      });
      if (!res.ok) throw new Error("Failed to dismiss");
      
      setEmployees((prev) => prev.filter((emp) => emp.memberDisplayName !== employeeToDismiss));
      addNotification(`Employee ${employeeToDismiss} has been terminated.`);
    } catch (err) {
      addNotification(`Error: ${err.message}`);
    } finally {
      setIsProcessing(false);
      setShowConfirm(false);
      setEmployeeToDismiss(null);
    }
  };

// --- Inside MyCompany Component ---

  const renderRoster = () => {
    if (loading) return <p className="loading-text">LOADING...</p>;

    return (
      <div className="issue-tab-container roster-container">
        <div className="roster-list-container">
          {employees.map((emp, idx) => {
            const isMe = emp.memberDisplayName === currentUserDisplayName;
            return (
              <div key={idx} className="personnel-card">
                {/* Info Section */}
                <div 
                  className="personnel-info" 
                  onClick={() => setPlayerSelected(emp.memberDisplayName)}
                >
                  <div className="name-row">
                    <span className="name-text">{emp.memberDisplayName}</span>
                    {isMe && <span className="self-tag">YOU</span>}
                  </div>
                  <span className="rank-text">{emp.memberRank}</span>
                </div>

                {/* Operations Section */}
                <div className="personnel-actions">
                  {userCEO && !isMe && (
                    <>
                      <button 
                        className="action-link transfer"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent opening profile modal
                          setSuccessorTarget(emp.memberDisplayName);
                          setShowSuccessorConfirm(true);
                        }}
                      >
                        TRANSFER AUTHORITY
                      </button>
                      <button 
                        className="action-link terminate"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEmployeeToDismiss(emp.memberDisplayName);
                          setShowConfirm(true);
                        }}
                      >
                        TERMINATE
                      </button>
                    </>
                  )}
                  
                  {isMe && (
                    <button 
                      className="action-link resign" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowResignConfirm(true);
                      }}
                    >
                      RESIGN POSITION
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
const hasCompany = companyDoc !== null;

  return (
    <div className="company-main-panel">
      {/* Navigation Headers */}
      {hasCompany && (
<div className="tab-navigation-container">
  <div className="nav-modules-row">
    <div 
      className={`nav-module ${windowState === "Roster" ? "active" : ""}`} 
      onClick={() => setWindowState("Roster")}
    >
      <span className="module-label">Personnel</span>
    </div>

    {userCEO && (
      <>
        <div 
          className={`nav-module ${windowState === "Logo" ? "active" : ""}`} 
          onClick={() => setWindowState("Logo")}
        >
          <span className="module-label">Logo</span>
        </div>
        <div 
          className={`nav-module ${windowState === "Details" ? "active" : ""}`} 
          onClick={() => setWindowState("Details")}
        >
          <span className="module-label">Description</span>
        </div>
        <div 
          className={`nav-module ${windowState === "Issue" ? "active" : ""}`} 
          onClick={() => setWindowState("Issue")}
        >
          <span className="module-label">Issue</span>
        </div>
        <div 
          className={`nav-module ${windowState === "Dividends" ? "active" : ""}`} 
          onClick={() => setWindowState("Dividends")}
        >
          <span className="module-label">Dividends</span>
        </div>
      </>
    )}
  </div>
</div>
      )}

      <div className="content-viewport">
        {hasCompany ? (
          <>
            {windowState === "Roster" && renderRoster()}
            {windowState === "Logo" && userCEO && <CompanyLogoEditor companyDoc={companyDoc} />}
            {windowState === "Details" && userCEO && (
              <CompanyDetailsTab companyDoc={companyDoc} setCompanyDoc={setCompanyDoc} userCEO={userCEO} />
            )}
            {windowState === "Issue" && userCEO && companyDoc && <CompanyIssueTab companyDoc={companyDoc} />}
            {windowState === "Dividends" && userCEO && companyDoc && <CompanyDividendsTab companyDoc={companyDoc} />}
          </>
        ) : (
          <FoundCompanyTab userId={userId} />
        )}
      </div>

      {/* MODALS & OVERLAYS */}
      {playerSelected && (
        <PlayerProfileModal playerSelected={playerSelected} onClose={() => setPlayerSelected(null)} />
      )}

      {showConfirm && (
        <ConfirmModal
          title="TERMINATION"
          message={<>Are you sure you want to dismiss <span className="text-[#50E3C2]">{employeeToDismiss}</span>?</>}
          confirmText="Confirm"
          cancelText="Cancel"
          onConfirm={dismissEmployee}
          onCancel={() => setShowConfirm(false)}
          isProcessing={isProcessing}
        />
      )}

      {showSuccessorConfirm && (
        <ConfirmModal
          title="TRANSFER"
          message={
            <>
              Promote <span className="text-[#50E3C2]">{successorTarget}</span> to CEO? 
              <br /><br />
              <span className="text-[#ff4d4d] text-xs">WARNING: This action is irreversible. Your executive privileges will be terminated.</span>
            </>
          }
          confirmText="Confirm"
          cancelText="Cancel"
          onConfirm={() => executeResignOrTransfer(successorTarget)}
          onCancel={() => setShowSuccessorConfirm(false)}
          isProcessing={isProcessing}
        />
      )}

{showResignConfirm && (
  <ConfirmModal
    title={userCEO && employees.length <= 1 ? "CORPORATE LIQUIDATION" : "RESIGNATION"}
    message={
      userCEO && employees.length > 1 
        ? "You cannot step down while other members exist. You must appoint a successor or dismiss all members first."
        : userCEO && employees.length <= 1 
          ? `Resigning will relinquish the company to NTCs. A fee of $${(relinquishFee || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} will be deducted from your wallet. Proceed?`
          : "Are you sure you wish to resign? All corporate access will be revoked."
    }
    confirmText={userCEO && employees.length > 1 ? "OK" : "Confirm"}
    cancelText="Cancel"
    onConfirm={userCEO && employees.length > 1 
      ? () => setShowResignConfirm(false) 
      : () => executeResignOrTransfer("")
    }
    onCancel={() => setShowResignConfirm(false)}
    isProcessing={isProcessing}
  />
)}
    </div>
  );
}